import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, LogIn } from "lucide-react";
import toast from "react-hot-toast";
import { InteractionStatus } from "@azure/msal-browser";
import { staffMsalInstance, staffLoginScopes } from "@/auth/msalConfig";
import { authApi } from "@/api/endpoints";
import { useAuthStore } from "@/store/auth";

export default function StaffLoginPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [loading, setLoading] = useState(false);
  const [inProgress, setInProgress] = useState<InteractionStatus>(InteractionStatus.None);

  useEffect(() => {
    // Clear any stale "interaction in progress" state from previous failed attempts
    // before initializing MSAL — these keys block subsequent loginPopup() calls.
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith("msal.") && k.includes("interaction.status"))
      .forEach((k) => sessionStorage.removeItem(k));

    staffMsalInstance
      .initialize()
      .then(() => staffMsalInstance.handleRedirectPromise())
      .catch(() => {});
  }, []);

  const signIn = async () => {
    if (inProgress !== InteractionStatus.None) {
      toast("A sign-in attempt is already underway");
      return;
    }
    setLoading(true);
    setInProgress(InteractionStatus.Login);
    try {
      await staffMsalInstance.initialize();
      const result = await staffMsalInstance.loginPopup({
        scopes: staffLoginScopes,
        prompt: "select_account",   // always show the account picker
      });
      const token = result.idToken ?? result.accessToken;
      const data = await authApi.exchange(token, "staff");
      setAuth(data.access_token, data.refresh_token, data.user);
      toast.success(`Welcome, ${data.user.first_name || data.user.email}`);
      navigate("/staff", { replace: true });
    } catch (err: unknown) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Sign-in failed";
      toast.error(message);
    } finally {
      setInProgress(InteractionStatus.None);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-md bg-slate-800 rounded-xl border border-slate-700 p-8 text-slate-100">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-brand-500 flex items-center justify-center">
            <ShieldCheck className="text-white" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">UmdoniWay Staff</h1>
            <p className="text-sm text-slate-400">Internal administration portal</p>
          </div>
        </div>
        <h2 className="text-2xl font-bold mb-2">Staff sign-in</h2>
        <p className="text-sm text-slate-400 mb-6">Authenticate with your @umdoniway.com Microsoft account.</p>
        <button onClick={signIn} disabled={loading} className="btn-primary w-full py-3" type="button">
          <LogIn size={18} />
          {loading ? "Signing in…" : "Sign in with Microsoft"}
        </button>
        <div className="mt-6 pt-6 border-t border-slate-700 text-center">
          <a href="/" className="text-sm text-slate-400 hover:text-brand-300">← Customer sign-in</a>
        </div>
      </div>
    </div>
  );
}
